import { RootStateType } from '@Store';
import { Box, Chip, Grid, Typography, Paper, useTheme, alpha } from '@mui/material';
import { CalendarToday, LocalShipping, Person } from '@mui/icons-material';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import DownloadShipmentLabelAndInvoice from '@Components/DownloadShipmentLabelAndInvoice';
import ShipmentKycUpload from '@Components/ShipmentKycUpload';
import { CSP_TYPES } from '@Constants/Shipment';

function Overview({ EditPermissionAllowed }: { EditPermissionAllowed?: boolean }) {
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment?.shipmentById);
  const [documentUploadModalOpen, setDocumentUploadModalOpen] = useState(false);
  const theme = useTheme();

  if (!shipmentById) return <></>;

  const shipmentNumber = shipmentById?.shipmentNumber || '';
  const WritePermission = EditPermissionAllowed ?? true;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2,
        borderRadius: 3,
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          transform: 'translateY(-2px)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(
            theme.palette.secondary.main,
            0.05,
          )} 50%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Grid container spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid item xs={12} md={2.5} lg={2.2}>
          <ShipmentNumberAndManifest />
        </Grid>

        <Grid item xs={12} md={5} lg={4}>
          <ShipmentPlaceDetails />
        </Grid>

        <Grid item xs={12} md={4} lg={3}>
          <PlaceDate />
        </Grid>

        <Grid item xs={12} md={12} lg={2.5}>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}>
            {WritePermission && (
              <DownloadShipmentLabelAndInvoice
                handleView={() => setDocumentUploadModalOpen(true)}
                shipmentNumber={shipmentById?.shipmentNumber}
                carrierAWBIcon={
                  shipmentById?.vendorShipmentModel &&
                  (shipmentById?.buyingPricing?.code === CSP_TYPES.fedex.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.ithinklogistics.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.ithink_logistics_domestic.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.linex.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.delhivery.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.trackon.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.movin_ups.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.dpduk.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.dhlIndia.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.dhl.value ||
                    shipmentById?.buyingPricing?.code === 'cirro' ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.stage.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.brightlinkers.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.universal.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.aramex.value)
                    ? shipmentById?.buyingPricing?.serviceImage
                    : ''
                }
              />
            )}
          </Box>
        </Grid>
      </Grid>

      {documentUploadModalOpen && (
        <ShipmentKycUpload
          shipmentNumber={shipmentNumber}
          open={documentUploadModalOpen}
          handleClose={() => setDocumentUploadModalOpen(false)}
        />
      )}
    </Paper>
  );
}

function ShipmentNumberAndManifest() {
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.5px',
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            mb: 0.25,
          }}
        >
          {shipmentById?.shipmentNumber || '—'}
        </Typography>
        <Chip
          label={`Manifest: ${shipmentById?.manifestNumber || '—'}`}
          size="small"
          variant="outlined"
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            borderColor: alpha(theme.palette.secondary.main, 0.5),
            color: theme.palette.secondary.main,
            height: 24,
            borderRadius: 1.5,
            backdropFilter: 'blur(8px)',
            background: alpha(theme.palette.secondary.main, 0.05),
            '& .MuiChip-label': { px: 1.2 },
          }}
        />
      </Box>
    </Box>
  );
}

function ShipmentPlaceDetails() {
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const placedBy = shipmentById?._placedById;
  const onBehalfBy = shipmentById?._userId;
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1,
          borderRadius: 2,
          background: alpha(theme.palette.primary.main, 0.04),
          backdropFilter: 'blur(8px)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        <Person sx={{ fontSize: 20, color: theme.palette.primary.main, opacity: 0.9 }} />
        <Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500, display: 'block' }}>
            Placed by
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            {placedBy?.name || '—'}
            <Box
              component="span"
              sx={{
                ml: 1,
                px: 1,
                py: 0.3,
                borderRadius: 1,
                background: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontSize: '0.7rem',
                fontWeight: 600,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              {placedBy?.accessGroup === 'pu_all' ? placedBy?.email : placedBy?.username || '—'}
            </Box>
          </Typography>
        </Box>
      </Box>

      {placedBy?.accessGroup !== 'pu_all' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1,
            borderRadius: 2,
            background: alpha(theme.palette.secondary.main, 0.04),
            backdropFilter: 'blur(8px)',
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
          }}
        >
          <LocalShipping sx={{ fontSize: 20, color: theme.palette.secondary.main, opacity: 0.9 }} />
          <Box>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary, fontWeight: 500, display: 'block' }}
            >
              On behalf of
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              {onBehalfBy?.name || '—'}
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 1,
                  py: 0.3,
                  borderRadius: 1,
                  background: alpha(theme.palette.secondary.main, 0.1),
                  color: theme.palette.secondary.main,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                }}
              >
                {onBehalfBy?.email || '—'}
              </Box>
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function PlaceDate() {
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const date = shipmentById?.createdAt ? moment(shipmentById?.createdAt).format('DD MMM YYYY, hh:mm a') : '';
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        background: alpha(theme.palette.primary.main, 0.04),
        backdropFilter: 'blur(8px)',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      }}
    >
      <CalendarToday sx={{ fontSize: 20, color: theme.palette.primary.main, opacity: 0.9 }} />
      <Box>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500, display: 'block' }}>
          Created at
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {date || '—'}
        </Typography>
      </Box>
    </Box>
  );
}

export default Overview;
